import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { t, lang } = useLang();
  const suggestions = t("chat.suggestions");
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`s-${Math.random().toString(36).slice(2, 10)}`);
  const listRef = useRef(null);
  const greetedLang = useRef(null);

  useEffect(() => {
    if (greetedLang.current !== lang) {
      greetedLang.current = lang;
      setMessages([{ role: "assistant", text: t("chat.greeting") }]);
    }
  }, [lang, t]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, text, lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const { c } = JSON.parse(payload);
            if (c) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + c };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: t("chat.error"),
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const streamingEmpty = busy && messages.length > 0 && messages[messages.length - 1].text === "";

  return (
    <>
      <motion.button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("chat.openAria")}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 3.2, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-[#8C9A86] text-[#0B0C0E] shadow-xl shadow-black/40 transition-colors duration-200 hover:bg-[#9FAF98]"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            data-lenis-prevent
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[70] flex h-[520px] max-h-[72vh] flex-col overflow-hidden rounded-2xl border border-[#262930] bg-[#121418] shadow-2xl shadow-black/50 sm:inset-x-auto sm:right-5 sm:bottom-24 sm:w-[380px]"
          >
            <div className="flex items-center justify-between border-b border-[#262930] px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4ADE80]" />
                </span>
                <span className="font-display text-sm font-bold tracking-tight text-[#EAE6E1]">
                  {t("chat.title")}
                </span>
              </div>
              <span className="font-tech text-[9px] uppercase tracking-[0.15em] text-[#686D76]">
                {t("chat.modelTag")}
              </span>
            </div>

            <div ref={listRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={
                      m.role === "user"
                        ? "max-w-[85%] rounded-xl rounded-br-sm bg-[#8C9A86] px-3.5 py-2.5 text-sm leading-relaxed text-[#0B0C0E]"
                        : "max-w-[85%] rounded-xl rounded-tl-sm border border-[#262930] bg-[#181A20] px-3.5 py-2.5 text-sm leading-relaxed text-[#EAE6E1]"
                    }
                  >
                    {m.text || (streamingEmpty && i === messages.length - 1 ? "…" : m.text)}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#3A3F4B] px-3 py-1.5 text-xs text-[#9E9E9E] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-[#262930] p-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("chat.placeholder")}
                  className="flex-1 rounded-lg border border-[#262930] bg-[#0E1013] px-3.5 py-2.5 text-base sm:text-sm text-[#EAE6E1] placeholder:text-[#686D76] outline-none transition-colors duration-200 focus:border-[#8C9A86]"
                />
                <button
                  data-testid="chat-send-button"
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  aria-label={t("chat.sendAria")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#8C9A86] text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98] disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-center font-tech text-[8px] uppercase tracking-[0.15em] text-[#686D76]">
                {t("chat.note")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
